import Rect from "./Rect";
import { ComponentElementPosition } from "@bizform/type/model/data_set/LookUp.model";
import { TabSet } from "./TabSet";
import { JSMap } from "@bizform/type/model/component_element/ComponentElement.model";

/**
 * TabView 컴포넌트의 Tab 사이즈 및 숨김 기능과 같은 로직 구현을 위한 helper(utility) class
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @class
 */
class TabControlHelper {

    private static CORRECTION_NAVBAR_WIDTH_VALUE = 20;
    private static CORRECTION_TAB_WIDTH_VALUE = 2;

    // TabSet 인스턴스의 포인터
    private _tabSetInstance: TabSet;
    private _tabNavRect: Rect;
    // 각각의 tab element들에 대한 BoundingClientRect 객체 정보를 담고 있는 Array
    private _tabRects: Array<Rect>;
    private _initTabNavHeight: number;

    constructor(tabSetInstance: TabSet) {
        this._tabSetInstance = tabSetInstance;
        this._tabNavRect = null;
        this._tabRects = new Array<Rect>();
        this._initTabNavHeight = -1;
    }

    // setter, getter
    public getTabNavRect(): Rect {
        return this._tabNavRect;
    }

    public getTabRects(): Array<Rect> {
        return this._tabRects;
    }

    public getTabRectWidth(index: number): number {
        return this._tabRects[index].width;
    }

    public getTabRectHeight(index: number): number {
        return this._tabRects[index].height;
    }

    public setInitTabNavHeight(height: number): void {
        this._initTabNavHeight = height;
    }

    /**
     * tab들의 visibility 계산 및 수행
     */
    public performTabVisibilitySetting(): Array<boolean> {
        if (!this._operationSafetyCheck()) return;

        this._updateNavRect();

        if (!this._tabSetInstance.getProps().getCalcVisibilityDone()) {
            this._updateRect();
        }

        return this._calcVisibleTab();
    }

    private _updateNavRect(): void {
        const tabNavClientRect = (this._tabSetInstance.getTabNavRef() as Element).getBoundingClientRect();
        this._tabNavRect = new Rect(tabNavClientRect.left, tabNavClientRect.top, tabNavClientRect.width, tabNavClientRect.height);
    }

    /**
     * Tab Navigation bar와 Tab들의 width(height), 차지하는 영역등을 계산한다.
     */
    private _updateRect(): void {
        if (!this._operationSafetyCheck()) return;

        this._tabRects.length = 0;
        // console.log(this._tabSetInstance.getTabRefs());
        for (let idx = 0; idx < this._tabSetInstance.getTabRefs().length; ++idx) {
            if (!this._tabSetInstance.getTabRefs()[idx]) {
                // this._tabRects.push(new Rect(0, 0, 0, 0));
                // Debug.console.error("Tab ref[", idx, "] is not invalid.");
                // Debug.console.error(this._tabSetInstance.getTabRefs()[idx]);

                continue;
            }

            const tabClientRect = (this._tabSetInstance.getTabRefs()[idx] as Element).getBoundingClientRect();
            const tabRect = new Rect(tabClientRect.left, tabClientRect.top, tabClientRect.width, tabClientRect.height);

            if (idx === 0 || tabRect.x > this._tabRects[idx - 1].vEndX) {
                if (idx === 0) {
                    tabRect.vStartX = 0;
                    tabRect.vEndX = tabRect.width;
                    tabRect.vStartY = 0;
                    tabRect.vEndY = tabRect.height;
                } else {
                    tabRect.vStartX = this._tabRects[idx - 1].vEndX + TabControlHelper.CORRECTION_TAB_WIDTH_VALUE;
                    tabRect.vEndX = tabRect.vStartX + tabRect.width;
                    tabRect.vStartY = this._tabRects[idx - 1].vEndY + TabControlHelper.CORRECTION_TAB_WIDTH_VALUE;
                    tabRect.vEndY = tabRect.vStartY + tabRect.height;
                }
            } else {
                // 초기 로딩 시 wrap이 된 경우
                const previousRect = this._tabRects[idx - 1];
                tabRect.vStartX = previousRect.vEndX + TabControlHelper.CORRECTION_TAB_WIDTH_VALUE;
                tabRect.vEndX = tabRect.vStartX + tabRect.width;

                tabRect.vStartY = previousRect.vEndY + TabControlHelper.CORRECTION_TAB_WIDTH_VALUE;
                tabRect.vEndY = tabRect.vStartY + tabRect.height;
            }

            this._tabRects.push(tabRect);
        }

        this._tabSetInstance.getProps().setCalcVisibilityDone(true);
        // console.log(this._tabRects);
    }

    /**
     * 보여야 할 탭과 보이지 말아야 할 탭을 세팅.
     * TODO: 로직 & 성능 개선 검토
     *
     * 1) activeItem의 startX와 endX가 tabNav의 width에 완전 포함되는가?
     * 2) if 완전 포함 activeItem 이후의 item.startX가 tabNav.width보다 크면 숨김 상태 세팅
     *    else activeItem 이후의 item은 모두 숨김 상태 세팅
     * 3) item.startX < activeItem.endX - tabNav.width 이면 숨김 상태 세팅
     *    단, 숨김/보임 상태 세팅은 primereact Tab 자체의 숨김/보임 기능이 있는 경우 같이 고려되어야 한다.
     */
    private _calcVisibleTab(): Array<boolean> {
        const tabPosition = this._tabSetInstance.getProps().tabPosition;
        // const activeIndex = this._tabSetInstance.getState().activeIndex;
        const activeIndex = this._tabSetInstance.getProps().activeIndex;
        const newTabVisibilities: Array<boolean> = new Array<boolean>();
        newTabVisibilities.length = this._tabRects.length;

        // 영역의 크기를 모를때는 다 표현
        if (this._tabSetInstance.getProps().accessibility || this._tabRects[0] && this._tabRects[0].width === 0) {
            for (let i = 0; i < newTabVisibilities.length; i++) {
                newTabVisibilities[i] = true;
            }
            return newTabVisibilities;
        }

        let navHeight: number;

        if (this._initTabNavHeight === -1) {
            navHeight = this._initTabNavHeight;
        } else {
            navHeight = this._tabNavRect.height;
        }

        const tabNavRectSize = (tabPosition === ComponentElementPosition.TOP || tabPosition === ComponentElementPosition.BOTTOM) ?
            this._tabNavRect.width : navHeight;
        let sumAllowSize = 0;
        let falsyBeforeIdx = -1;
        const lastIndex = newTabVisibilities.length - 1;

        for (let idx = lastIndex; idx >= 0; --idx) {
            if (idx < lastIndex) {
                const nextTabVisibility = newTabVisibilities[idx + 1];
                if (idx > activeIndex && nextTabVisibility) {
                    newTabVisibilities[idx] = true;
                    continue;
                }

                if (idx < activeIndex && !nextTabVisibility) {
                    newTabVisibilities[idx] = false;
                    continue;
                }
            }

            const tabRectSize = (tabPosition === ComponentElementPosition.TOP || tabPosition === ComponentElementPosition.BOTTOM) ?
                this._tabRects[idx].width : this._tabRects[idx].height;

            if (idx === activeIndex) {
                newTabVisibilities[idx] = true;
                sumAllowSize += tabRectSize;
            } else {
                const vEnd = (tabPosition === ComponentElementPosition.TOP || tabPosition === ComponentElementPosition.BOTTOM) ?
                    this._tabRects[idx].vEndX : this._tabRects[idx].vEndY;

                if (vEnd + TabControlHelper.CORRECTION_NAVBAR_WIDTH_VALUE >= tabNavRectSize) {
                    if (idx > activeIndex) {
                        newTabVisibilities[idx] = false;
                    } else {
                        if (tabNavRectSize > sumAllowSize + tabRectSize + TabControlHelper.CORRECTION_NAVBAR_WIDTH_VALUE) {
                            newTabVisibilities[idx] = true;
                            sumAllowSize += tabRectSize;
                        } else {
                            newTabVisibilities[idx] = false;
                        }
                    }
                } else {
                    if (idx < falsyBeforeIdx) {
                        newTabVisibilities[idx] = false;
                    } else {
                        if (tabNavRectSize > sumAllowSize + tabRectSize + TabControlHelper.CORRECTION_NAVBAR_WIDTH_VALUE) {
                            newTabVisibilities[idx] = true;
                            sumAllowSize += tabRectSize;
                        } else {
                            newTabVisibilities[idx] = false;
                            falsyBeforeIdx = idx;
                        }
                    }
                }
            }
        }

        return newTabVisibilities;
    }

    /**
     * 숨김 탭 목록 버튼의 스타일을 반환
     */
    public getHidTabBoxStyleWithPosition(): JSMap<any> {

        // 최초 로딩 시에는 this._tabRects가 세팅되지 않는다.
        if (!this._tabSetInstance.getProps().getCalcVisibilityDone()) {
            return {};
        }

        const hidTabBoxStyle: JSMap<any> = {};
        const tabPosition = this._tabSetInstance.getProps().tabPosition;
        let activeIndex = this._tabSetInstance.getState().activeIndex;
        // 활성화할 탭의 인덱스가 탭들이 삭제되어서 존재하지 않는 경우
        activeIndex = (activeIndex > this._tabRects.length - 1) ? 0 : activeIndex;
        const tabClientRect = this._tabRects[activeIndex];

        hidTabBoxStyle.position = "absolute";
        hidTabBoxStyle.padding = 0;
        hidTabBoxStyle.zIndex = 1;
        hidTabBoxStyle.fontSize = "0.7em";

        if (tabPosition === ComponentElementPosition.TOP) {
            hidTabBoxStyle.width = tabClientRect.height + "px";
            hidTabBoxStyle.height = tabClientRect.height + "px";
            hidTabBoxStyle.top = 0;
            hidTabBoxStyle.right = 0;
        } else if (tabPosition === ComponentElementPosition.RIGHT) {
            hidTabBoxStyle.width = tabClientRect.width + "px";
            hidTabBoxStyle.height = tabClientRect.width + "px";
            hidTabBoxStyle.bottom = 0;
            hidTabBoxStyle.right = 0;
        } else if (tabPosition === ComponentElementPosition.BOTTOM) {
            hidTabBoxStyle.width = tabClientRect.height + "px";
            hidTabBoxStyle.height = tabClientRect.height + "px";
            hidTabBoxStyle.bottom = 0;
            hidTabBoxStyle.right = 0;
        } else {
            hidTabBoxStyle.width = tabClientRect.width + "px";
            hidTabBoxStyle.height = tabClientRect.width + "px";
            hidTabBoxStyle.bottom = 0;
            hidTabBoxStyle.left = 0;
        }

        return hidTabBoxStyle;
    }

    /**
     * Navigation bar의 높이 또는 폭을 반환한다.
     * resize 시 overflow hidden 속성을 설정하기 위해서이다.
     */
    public getAllowedNavbarSizeAsStyle(): JSMap<any> {
        const style: JSMap<any> = {};
        style.overflow = "hidden";
        const tabPosition = this._tabSetInstance.getProps().tabPosition;

        if (this._operationSafetyCheck()) {
            const tabRefs = this._tabSetInstance.getTabRefs();
            if (tabPosition === ComponentElementPosition.TOP || tabPosition === ComponentElementPosition.BOTTOM) {
                const height = this._tabRects && this._tabRects[0] ? this._tabRects[0].height : 0;
                if (height > 0) {
                    style.height = height + "px";
                }
            } else {
                const width = this._tabRects && this._tabRects[0] ? this._tabRects[0].width : 0;
                if (width > 0) {
                    style.width = width + "px";
                }
            }
        }

        return style;
    }

    /**
     * _tabNavRef와 _tabRefs가 할당되어있는지 체크
     *
     * @private
     */
    private _operationSafetyCheck(): boolean {
        if (this._tabSetInstance.getTabNavRef() && this._tabSetInstance.getTabRefs()) {
            return true;
        } else {
            return false;
        }
    }
}

export default TabControlHelper;
